import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import type { ChatSession, ChatMessage, Citation } from '@/types';

function transformCitations(citations: {
  documentId: string;
  documentTitle: string;
  page?: number;
  section?: string;
  sectionNumber?: string;
  timestampStart?: number;
  timestampEnd?: number;
  snippet?: string;
}[]): Citation[] {
  return citations.map((c) => ({
    document_id: c.documentId,
    document_title: c.documentTitle,
    page: c.page,
    section: c.section,
    section_number: c.sectionNumber,
    timestamp: c.timestampStart,
    text_snippet: c.snippet,
  }));
}

interface ChatState {
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  streamingMessageId: string | null;
  _abortController: AbortController | null;
  setSessions: (sessions: ChatSession[]) => void;
  setCurrentSession: (session: ChatSession | null) => void;
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  fetchSessions: (userId: string) => Promise<void>;
  createSession: (userId: string, title?: string, documentIds?: string[]) => Promise<ChatSession | null>;
  loadSession: (sessionId: string) => Promise<void>;
  sendMessage: (content: string, userId: string) => Promise<void>;
  cancelMessage: () => void;
  deleteSession: (sessionId: string) => Promise<void>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  sessions: [],
  currentSession: null,
  messages: [],
  isLoading: false,
  error: null,
  streamingMessageId: null,
  _abortController: null,

  setSessions: (sessions) => set({ sessions }),
  setCurrentSession: (currentSession) => set({ currentSession }),
  setMessages: (messages) => set({ messages }),
  addMessage: (message) => set((state) => ({ messages: [...state.messages, message] })),

  fetchSessions: async (userId: string) => {
    const { data, error } = await supabase
      .from('chat_sessions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'fetchSessions');
      set({ error: error.message });
      return;
    }

    set({ sessions: data || [] });
  },

  createSession: async (userId: string, title?: string, documentIds?: string[]) => {
    Sentry.addBreadcrumb({ category: 'chat', message: 'Session created', level: 'info' });
    const newSession = {
      user_id: userId,
      title: title || 'New Chat',
      document_ids: documentIds || [],
      created_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('chat_sessions')
      .insert(newSession)
      .select()
      .single();

    if (error) {
      reportSupabaseError(error, 'createSession');
      set({ error: error.message });
      return null;
    }

    set((state) => ({
      sessions: [data, ...state.sessions],
      currentSession: data,
      messages: [],
    }));

    return data;
  },

  loadSession: async (sessionId: string) => {
    set({ isLoading: true });

    const { data: session, error: sessionError } = await supabase
      .from('chat_sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (sessionError) {
      reportSupabaseError(sessionError, 'loadSession.session');
      set({ error: sessionError.message, isLoading: false });
      return;
    }

    const { data: messages, error: messagesError } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (messagesError) {
      reportSupabaseError(messagesError, 'loadSession.messages');
      set({ error: messagesError.message, isLoading: false });
      return;
    }

    set({
      currentSession: session,
      messages: messages || [],
      isLoading: false,
    });
  },

  sendMessage: async (content: string, userId: string) => {
    Sentry.addBreadcrumb({ category: 'chat', message: 'Message sent', level: 'info' });
    let session = get().currentSession;

    // Create session if none exists
    if (!session) {
      session = await get().createSession(userId, content.slice(0, 50));
      if (!session) return;
    }

    // Add user message optimistically to UI
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    get().addMessage(userMessage);

    // Note: Edge function saves messages to DB, so we skip duplicate insert

    const abortController = new AbortController();
    set({ isLoading: true, error: null, _abortController: abortController });

    await Sentry.startSpan(
      { name: 'chat.sendMessage', op: 'ai.pipeline' },
      async () => {
        // Get auth token for raw fetch
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const token = authSession?.access_token;
        if (!token) {
          set({ error: 'Not authenticated', isLoading: false, _abortController: null });
          return;
        }

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        let response: Response;
        try {
          response = await fetch(`${supabaseUrl}/functions/v1/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'apikey': anonKey,
            },
            body: JSON.stringify({
              question: content,
              sessionId: session!.id,
              documentIds: session!.document_ids,
            }),
            signal: abortController.signal,
          });
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          set({ error: 'Connection error', isLoading: false, _abortController: null });
          return;
        }

        // Handle non-streaming error responses (400/401/404/429/502)
        if (!response.ok) {
          let errMsg = 'Request failed';
          try {
            const errData = await response.json();
            errMsg = errData.error || errData.message || errMsg;
          } catch { /* ignore parse failure */ }
          set({ error: errMsg, isLoading: false, _abortController: null });
          return;
        }

        // Add placeholder assistant message for streaming
        const streamingId = crypto.randomUUID();
        const streamingMessage: ChatMessage = {
          id: streamingId,
          role: 'assistant',
          content: '',
          created_at: new Date().toISOString(),
        };
        set((state) => ({
          messages: [...state.messages, streamingMessage],
          streamingMessageId: streamingId,
        }));

        // Read the SSE stream
        if (!response.body) {
          set((state) => ({
            messages: state.messages.filter((m) => m.id !== streamingId),
            error: 'Response body unavailable',
            isLoading: false,
            _abortController: null,
            streamingMessageId: null,
          }));
          return;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop()!;

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                currentEvent = line.slice(7).trim();
              } else if (line.startsWith('data: ')) {
                const raw = line.slice(6).trim();
                try {
                  const parsed = JSON.parse(raw);

                  if (currentEvent === 'chunk' && parsed.content) {
                    set((state) => ({
                      messages: state.messages.map((m) =>
                        m.id === streamingId
                          ? { ...m, content: m.content + parsed.content }
                          : m
                      ),
                    }));
                  } else if (currentEvent === 'citations' && parsed.citations) {
                    set((state) => ({
                      messages: state.messages.map((m) =>
                        m.id === streamingId
                          ? { ...m, citations: transformCitations(parsed.citations) }
                          : m
                      ),
                    }));
                  } else if (currentEvent === 'error') {
                    set({ error: parsed.message || 'Stream error' });
                  }
                } catch {
                  // Skip malformed data lines
                }
                currentEvent = '';
              }
            }
          }
        } catch (err) {
          // Remove partial message on any stream error
          set((state) => ({
            messages: state.messages.filter((m) => m.id !== streamingId),
          }));
          if (err instanceof Error && err.name === 'AbortError') {
            set({ isLoading: false, _abortController: null, streamingMessageId: null });
            return;
          }
          set({ error: 'The response was interrupted. Please try again.' });
        } finally {
          set({ isLoading: false, _abortController: null, streamingMessageId: null });
        }

        // Update session title if first message
        if (get().messages.length <= 2) {
          await supabase
            .from('chat_sessions')
            .update({ title: content.slice(0, 50) })
            .eq('id', session!.id);
        }
      }
    );
  },

  cancelMessage: () => {
    const { _abortController } = get();
    if (_abortController) {
      _abortController.abort();
    }
    set({ isLoading: false, _abortController: null, streamingMessageId: null });
  },

  deleteSession: async (sessionId: string) => {
    await supabase.from('chat_messages').delete().eq('session_id', sessionId);
    await supabase.from('chat_sessions').delete().eq('id', sessionId);

    set((state) => ({
      sessions: state.sessions.filter((s) => s.id !== sessionId),
      currentSession: state.currentSession?.id === sessionId ? null : state.currentSession,
      messages: state.currentSession?.id === sessionId ? [] : state.messages,
    }));
  },
}));
